import { useCallback } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CourseData } from '@/lib/learner/card-fields';
import { learnerQueryKey } from '@/lib/constants/query-keys';
import { fetchLearnerItems } from './fetch-learner';

/** Most recently viewed items as cards (GET /api/learner/recently-viewed). */

interface RecentlyViewedParams {
  userUid?: string;
  limit?: number;
  /** Only items with progress or completed (the dashboard's list). */
  startedOnly?: boolean;
}

/**
 * Shared by the query and its prefetch so they use one cache key. staleTime is short because
 * viewing a lesson changes this list.
 */
const recentlyViewedQuery = ({ userUid, limit = 3, startedOnly = false }: RecentlyViewedParams) => {
  const params = new URLSearchParams({ limit: String(limit), ...(startedOnly ? { started: '1' } : {}) });
  return queryOptions({
    queryKey: learnerQueryKey(userUid, 'recently-viewed', limit, startedOnly),
    queryFn: () => fetchLearnerItems<CourseData>(`recently-viewed?${params}`, 'Could not load recently viewed items'),
    staleTime: 15_000,
    gcTime: 10 * 60_000,
    retry: 1,
  });
};

export function useRecentlyViewed({ enabled, ...params }: RecentlyViewedParams & { enabled: boolean }) {
  return useQuery({ ...recentlyViewedQuery(params), enabled: enabled && Boolean(params.userUid) });
}

/** Start loading before the list is shown (e.g. on hover/focus of the search box). */
export function usePrefetchRecentlyViewed({ userUid, limit, startedOnly }: RecentlyViewedParams) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    if (!userUid) return;
    void queryClient.prefetchQuery(recentlyViewedQuery({ userUid, limit, startedOnly }));
  }, [queryClient, userUid, limit, startedOnly]);
}
